// Team access: who can open the dashboard, and who has been invited.
//
// Invites are never created or redeemed from the browser — both go through edge
// functions running with the service role, which is what makes the token
// single-use and unguessable. The client can only list and revoke.
import { supabase } from "@/lib/supabase";

export interface Member {
  user_id: string;
  role: string;
  email: string;
  full_name: string;
  last_sign_in_at: string | null;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  created_at: string;
}

export async function getMembers(): Promise<Member[]> {
  const { data, error } = await supabase
    .from("org_members_v")
    .select("user_id, role, email, full_name, last_sign_in_at")
    .order("email");
  if (error) throw error;
  return (data ?? []) as Member[];
}

export async function getPendingInvites(): Promise<PendingInvite[]> {
  const { data, error } = await supabase
    .from("pending_invites_v")
    .select("id, email, role, expires_at, created_at")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as PendingInvite[];
}

/** Mint + email an invite. The token exists only in the email. */
export async function inviteUser(email: string, role: "member" | "admin" = "member"): Promise<void> {
  const { data, error } = await supabase.functions.invoke("invite-user", {
    body: { email, role },
  });
  // Edge errors arrive as FunctionsHttpError; surface the server's message.
  if (error) {
    let msg = "Uitnodiging versturen mislukt.";
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx) msg = (await ctx.clone().json())?.error ?? msg;
    } catch { /* keep the fallback */ }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
}

export async function revokeInvite(id: string): Promise<void> {
  const { error } = await supabase.rpc("revoke_invite", { p_id: id });
  if (error) throw error;
}

/** Redeem an invite — called from the public /invite/:token page. */
export async function acceptInvite(
  token: string, password: string, fullName: string,
): Promise<string> {
  const { data, error } = await supabase.functions.invoke("accept-invite", {
    body: { token, password, full_name: fullName },
  });
  if (error) {
    let msg = "Deze uitnodiging is niet meer geldig.";
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx) msg = (await ctx.clone().json())?.error ?? msg;
    } catch { /* keep the fallback */ }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  return data.email as string;
}
